import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Ban, Loader2, RefreshCw, ShieldOff, Undo2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { NeedsReassignmentBadge, singleCaseReassignTarget } from "./NeedsReassignmentBadge";
import { ReassignDialog } from "./ReassignDialog";

const fmtDT = (iso) => { if (!iso) return ""; const d = new Date(iso); return isNaN(d) ? iso : d.toLocaleString("el-GR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); };

// Διάλογος «Εξαίρεση Δικαστή» ΜΟΝΟ από τη συγκεκριμένη υπόθεση
const ExcludeJudgeDialog = ({ open, onClose, info, onDone }) => {
  const [judge, setJudge] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setJudge(""); setReason(""); } }, [open]);
  const options = (info?.involved_judges || []).filter(n => !(info?.active_exclusions || []).includes(n));
  const submit = async () => {
    if (!judge) return;
    try {
      setBusy(true);
      const r = await axios.post(`${API}/cases/${info.case_id}/judge-exclusions`, { judge_name: judge, reason });
      toast.success(r.data?.message || "Ο δικαστής εξαιρέθηκε", { duration: 8000 });
      onDone?.(r.data); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα εξαίρεσης", { duration: 10000 });
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="exclude-judge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Ban className="w-5 h-5 text-red-700" />Εξαίρεση Δικαστή από την υπόθεση</DialogTitle>
          <DialogDescription>ΓΑΚ {info?.gak} · {info?.court_type}{info?.pinakio_label ? ` · ${info.pinakio_label}` : ""}{info?.hearing_date ? ` — ${info.hearing_date.split("-").reverse().join("/")}` : ""}
            <br /><span className="text-xs">Η εξαίρεση ισχύει ΜΟΝΟ για τη συγκεκριμένη υπόθεση (όχι για όλη τη δικάσιμο). Ο δικαστής δεν θα μπορεί να οριστεί Δικαστής Έδρας ή Εισηγητής/Προδικαστικού σε αυτήν.</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label className="text-xs">Δικαστής</Label>
            <Select value={judge} onValueChange={setJudge}>
              <SelectTrigger className="h-9" data-testid="exclude-judge-select"><SelectValue placeholder={options.length ? "Επιλέξτε δικαστή…" : "Δεν υπάρχουν εμπλεκόμενοι δικαστές"} /></SelectTrigger>
              <SelectContent>
                {options.map(n => (
                  <SelectItem key={n} value={n} data-testid={`exclude-judge-option-${n}`}>
                    {n}{n === info?.assigned_judge ? " — Δικαστής Προδικαστικού" : n === info?.bench_judge ? " — Δικαστής Έδρας" : (info?.composition_members || []).includes(n) ? " — μέλος Σύνθεσης" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Λόγος (προαιρετικά)</Label>
            <Textarea className="text-xs min-h-[70px]" value={reason} onChange={e => setReason(e.target.value)} placeholder="π.χ. κώλυμα άρθρου 52 ΚΠολΔ / συγγένεια με διάδικο" data-testid="exclude-judge-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="exclude-judge-cancel">Άκυρο</Button>
          <Button className="bg-red-700 hover:bg-red-800" onClick={submit} disabled={busy || !judge} data-testid="exclude-judge-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Ban className="w-4 h-4 mr-1" />Εξαίρεση</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Καρτέλα Υπόθεσης: ένδειξη «ΑΠΑΙΤΕΙΤΑΙ ΑΝΑΧΡΕΩΣΗ» + Εξαιρέσεις Δικαστών (ιστορικό, άρση από Admin) + ΑΝΑΧΡΕΩΣΗ της υπόθεσης
export const JudgeExclusionsCard = ({ caseId, isAdmin, onChanged }) => {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [reassign, setReassign] = useState(null);
  const [lifting, setLifting] = useState("");
  const load = useCallback(() => axios.get(`${API}/cases/${caseId}/judge-exclusions`).then(r => setInfo(r.data)).catch(() => {}), [caseId]);
  useEffect(() => { if (caseId) load(); }, [caseId, load]);
  const changed = () => { load(); onChanged?.(); };
  if (!info) return null;
  const active = (info.exclusions || []).filter(x => x.active !== false);
  const lifted = (info.exclusions || []).filter(x => x.active === false);
  const lift = async (x) => {
    if (!window.confirm(`Άρση εξαίρεσης του/της ${x.judge_name};`)) return;
    try {
      setLifting(x.id);
      const r = await axios.delete(`${API}/cases/${caseId}/judge-exclusions/${x.id}`);
      toast.success(r.data?.message || "Η εξαίρεση άρθηκε");
      changed();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα άρσης εξαίρεσης");
    } finally { setLifting(""); }
  };
  return (
    <div className="border-t pt-4" data-testid="judge-exclusions-section">
      {info.needs_reassignment && (
        <div className="mb-3 rounded border-2 border-red-600 bg-red-50 px-3 py-2 flex flex-wrap items-center gap-2" data-testid="case-needs-reassignment-banner">
          <NeedsReassignmentBadge need={info.needs_reassignment} caseId={caseId} />
          <span className="text-xs text-red-900">{info.needs_reassignment.reason}</span>
          {info.pinakio_label && info.hearing_date && (
            <Button size="sm" className="ml-auto h-7 text-xs gap-1 bg-orange-600 hover:bg-orange-700"
                    onClick={() => setReassign(singleCaseReassignTarget({ pinakioLabel: info.pinakio_label, hearingDate: info.hearing_date, courtType: info.court_type, caseId, benchJudge: info.bench_judge }))}
                    data-testid="case-reassign-now-btn">
              <RefreshCw className="w-3.5 h-3.5" />ΑΝΑΧΡΕΩΣΗ τώρα
            </Button>
          )}
        </div>
      )}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Label className="text-slate-500 font-semibold flex items-center gap-2"><ShieldOff className="w-4 h-4 text-red-700" />Εξαιρέσεις Δικαστών (μόνο για αυτή την υπόθεση)</Label>
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1 border-red-300 text-red-700 hover:bg-red-50" onClick={() => setOpen(true)} data-testid="exclude-judge-btn">
          <Ban className="w-3.5 h-3.5" />Εξαίρεση Δικαστή
        </Button>
      </div>
      {!active.length && !lifted.length && <p className="text-xs text-slate-400 italic mt-1" data-testid="judge-exclusions-empty">Καμία εξαίρεση.</p>}
      {active.length > 0 && (
        <ul className="mt-2 space-y-1" data-testid="judge-exclusions-active">
          {active.map(x => (
            <li key={x.id} className="text-xs rounded border border-red-200 bg-red-50 px-2 py-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid={`judge-exclusion-${x.id}`}>
              <span className="font-semibold text-red-800">Εξαιρέθηκε ο/η {x.judge_name}</span>
              {x.reason && <span className="text-slate-700">— {x.reason}</span>}
              <span className="text-slate-500">· από {x.excluded_by || "—"}, {fmtDT(x.excluded_at)}</span>
              {(x.was_pretrial_judge || x.was_bench_judge) && <span className="text-[10px] text-orange-800">({x.was_pretrial_judge ? "ήταν Δικαστής Προδικαστικού" : "ήταν Δικαστής Έδρας"})</span>}
              {isAdmin && (
                <Button variant="ghost" size="sm" className="h-6 text-[11px] ml-auto text-slate-600" disabled={lifting === x.id} onClick={() => lift(x)} data-testid={`lift-exclusion-${x.id}`}>
                  {lifting === x.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Undo2 className="w-3 h-3 mr-1" />Άρση</>}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {lifted.length > 0 && (
        <ul className="mt-1 space-y-0.5" data-testid="judge-exclusions-lifted">
          {lifted.map(x => (
            <li key={x.id} className="text-[11px] text-slate-400 line-through px-2">Εξαίρεση {x.judge_name} ({fmtDT(x.excluded_at)}) — άρθηκε από {x.lifted_by || "—"}, {fmtDT(x.lifted_at)}</li>
          ))}
        </ul>
      )}
      <ExcludeJudgeDialog open={open} onClose={() => setOpen(false)} info={info} onDone={changed} />
      <ReassignDialog target={reassign} onClose={() => setReassign(null)} onDone={changed} />
    </div>
  );
};

export default JudgeExclusionsCard;
